import { MutableRefObject, useCallback } from 'react';

interface IUseScrollObserverProps {
  isLoading: boolean;
  observerItem: MutableRefObject<IntersectionObserver | null>;
  onIntersection: () => void;
  threshold?: number;
}

const THRESHOLD = 0.8;

export const useScrollObserver = <THTMLElement extends HTMLElement>({
  isLoading,
  observerItem,
  onIntersection,
  threshold = THRESHOLD,
}: IUseScrollObserverProps) => {
  const itemRef = useCallback(
    (node: THTMLElement | null) => {
      if (isLoading) return;

      if (observerItem.current) {
        observerItem.current.disconnect();
      }

      observerItem.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            onIntersection();
          }
        },
        { threshold },
      );

      if (node) {
        observerItem.current.observe(node);
      }
      return () => observerItem.current?.disconnect();
    },
    [isLoading, observerItem, onIntersection, threshold],
  );

  return itemRef;
};
